import React, { useState, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import AntAPI from '../../utils/AntAPI';
import AntLocalization from '../../utils/AntLocalization';
import Stage1Upload from './Stage1Upload';
import Stage2Processing from './Stage2Processing';
import Stage3TabbedReview from './Stage3TabbedReview';
import './index.scss';

var aivaAPI = null;
function getAPI() {
    if (!aivaAPI) aivaAPI = new AntAPI('ai', 'AIVoucherAnalysis');
    return aivaAPI;
}

function IconSparkle() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2 14 9l7 2-7 2-2 7-2-7-7-2 7-2z" />
        </svg>
    );
}
function IconClose() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}
function IconCheck2() {
    return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

var SUBTITLES = {
    upload: { vi: 'Tải lên ảnh hoặc PDF — AI sẽ bóc tách & điền vào phiếu', en: 'Upload an image or PDF — AI will extract and fill the voucher', fr: 'Importez une image ou un PDF — l’IA extrait et remplit le document', es: 'Sube una imagen o PDF — la IA extrae y rellena el comprobante', id: 'Unggah gambar atau PDF — AI akan mengekstrak & mengisi dokumen', ja: '画像または PDF をアップロード — AI が抽出して伝票に入力', zh: '上传图片或 PDF — AI 将提取并填入单据', ko: '이미지 또는 PDF 업로드 — AI가 추출해 전표에 입력', hi: 'इमेज या PDF अपलोड करें — AI डेटा निकालकर वाउचर भरेगा', ar: 'ارفع صورة أو PDF — يستخرج الذكاء الاصطناعي البيانات ويملأ المستند' },
    processing: { vi: 'AI đang đọc và bóc tách dữ liệu — vui lòng chờ', en: 'AI is reading and extracting data — please wait', fr: 'L’IA lit et extrait les données — veuillez patienter', es: 'La IA está leyendo y extrayendo datos — espera', id: 'AI sedang membaca & mengekstrak data — mohon tunggu', ja: 'AI がデータを読み取り・抽出中 — お待ちください', zh: 'AI 正在读取并提取数据 — 请稍候', ko: 'AI가 데이터를 읽고 추출 중 — 잠시 기다려 주세요', hi: 'AI डेटा पढ़कर निकाल रहा है — कृपया प्रतीक्षा करें', ar: 'الذكاء الاصطناعي يقرأ البيانات ويستخرجها — يرجى الانتظار' },
    review: { vi: 'Rà soát kết quả AI bóc tách trước khi áp dụng vào phiếu', en: 'Review AI extraction results before applying to voucher', fr: 'Vérifiez les résultats de l’IA avant de les appliquer au document', es: 'Revisa los resultados de la IA antes de aplicarlos', id: 'Tinjau hasil ekstraksi AI sebelum diterapkan ke dokumen', ja: '伝票に適用する前に AI の抽出結果を確認', zh: '应用到单据前请核对 AI 提取结果', ko: '전표에 적용하기 전에 AI 추출 결과 검토', hi: 'वाउचर में लागू करने से पहले AI के नतीजे जाँचें', ar: 'راجع نتائج استخراج الذكاء الاصطناعي قبل تطبيقها على المستند' }
};

function Stepper(props) {
    // stage: 'upload' | 'processing' | 'review'
    var stage = props.stage;
    function cls(target) {
        if (stage === target) return 'stepper-item active';
        var order = { upload: 1, processing: 2, review: 3 };
        if (order[stage] > order[target]) return 'stepper-item done';
        return 'stepper-item';
    }
    function dot(target, index) {
        var order = { upload: 1, processing: 2, review: 3 };
        if (order[stage] > order[target]) return <div className="dot"><IconCheck2 /></div>;
        return <div className="dot">{index}</div>;
    }
    return (
        <div className="stepper">
            <div className={cls('upload')}>{dot('upload', 1)}{AntLocalization.translateText({ vi: 'Tải lên', en: 'Upload', fr: 'Import', es: 'Subir', id: 'Unggah', ja: '取込', zh: '上传', ko: '업로드', hi: 'अपलोड', ar: 'رفع' })}</div>
            <div className="stepper-sep" />
            <div className={cls('processing')}>{dot('processing', 2)}{AntLocalization.translateText({ vi: 'Phân tích', en: 'Analyze', fr: 'Analyse', es: 'Análisis', id: 'Analisis', ja: '分析', zh: '分析', ko: '분석', hi: 'विश्लेषण', ar: 'تحليل' })}</div>
            <div className="stepper-sep" />
            <div className={cls('review')}>{dot('review', 3)}{AntLocalization.translateText({ vi: 'Rà soát', en: 'Review', fr: 'Révision', es: 'Revisión', id: 'Tinjau', ja: '確認', zh: '复核', ko: '검토', hi: 'समीक्षा', ar: 'مراجعة' })}</div>
        </div>
    );
}

var AntAIVoucherAnalyzer = forwardRef(function (props, ref) {
    var [visible, setVisible] = useState(false);
    var [stage, setStage] = useState('upload');
    var [analysisId, setAnalysisId] = useState(null);
    var [analysis, setAnalysis] = useState(null);
    var [error, setError] = useState(null);
    var [mode, setMode] = useState('Standard');
    var [initialFile, setInitialFile] = useState(null);
    // targetTable có thể đến từ prop tĩnh hoặc từ start(opts) khi mở standalone
    // (vd từ EXPGeneralOrders QuickWizard). Ưu tiên opts truyền vào start().
    var [runtimeTarget, setRuntimeTarget] = useState(null);
    var ctxRef = useRef({});

    var loadByCode = useCallback(function (code) {
        var api = getAPI();
        api.post('/' + api.routeURL + '/detail', { code: code }, function (resp) {
            var data = (resp && resp.data) || resp || {};
            setAnalysis(data);
            setAnalysisId(data.id);
            setStage(data.analysis_status === 'Done' ? 'review' : (data.analysis_status === 'Running' ? 'processing' : 'upload'));
        });
    }, []);

    useImperativeHandle(ref, function () {
        return {
            start: function (opts) {
                ctxRef.current = opts || {};
                setVisible(true);
                setError(null);
                setRuntimeTarget(opts && opts.targetTable ? {
                    targetTable: opts.targetTable,
                    targetCode: opts.targetCode || null,
                    targetId: opts.targetId || null
                } : null);
                setInitialFile((opts && opts.initialFile) || null);
                if (opts && opts.mode === 'review' && opts.analysisCode) {
                    setStage('processing');
                    loadByCode(opts.analysisCode);
                } else {
                    setStage('upload');
                    setAnalysisId(null);
                    setAnalysis(null);
                }
            },
            close: function () { setVisible(false); setInitialFile(null); }
        };
    });

    var handleAnalyzeStart = useCallback(function () {
        setError(null);
        setStage('processing');
    }, []);

    var handleAnalyzed = useCallback(function (data) {
        setAnalysisId(data.id);
        setAnalysis(data);
        if (data.apply_status === 'Applied' && data.target_code && data.target_table) {
            setVisible(false);
            if (props.onApplied) props.onApplied({
                ok: true,
                voucher_code: data.target_code,
                target_table: data.target_table,
                intent: 'CreateNew',
                auto_applied: true
            });
            return;
        }
        setStage(data.analysis_status === 'Failed' ? 'upload' : 'review');
        if (data.analysis_status === 'Failed') {
            setError(data.error_message || AntLocalization.translateText({ vi: 'Phân tích thất bại', en: 'Analysis failed', fr: 'Échec de l’analyse', es: 'Error en el análisis', id: 'Analisis gagal', ja: '解析に失敗しました', zh: '分析失败', ko: '분석 실패', hi: 'विश्लेषण विफल', ar: 'فشل التحليل' }));
        }
    }, [props.onApplied]);

    var handleAnalyzeFailed = useCallback(function (data) {
        setStage('upload');
        setError((data && (data.error_message || data.message || data.error)) ||
            AntLocalization.translateText({ vi: 'Phân tích thất bại', en: 'Analysis failed', fr: 'Échec de l’analyse', es: 'Error en el análisis', id: 'Analisis gagal', ja: '解析に失敗しました', zh: '分析失败', ko: '분석 실패', hi: 'विश्लेषण विफल', ar: 'فشل التحليل' }));
    }, []);

    var handleClose = useCallback(function () { setVisible(false); }, []);
    var handleApplied = useCallback(function (result) {
        setVisible(false);
        if (props.onApplied) props.onApplied(result);
    }, [props.onApplied]);

    if (!visible) return null;
    var stopEv = function (e) { e.stopPropagation(); };
    var portalNode = (
        <div className="ant-aiva-overlay"
            onClick={handleClose}
            onWheel={stopEv}
            onTouchMove={stopEv}
            onScroll={stopEv}
        >
            <div className="ant-aiva-popup"
                onClick={stopEv}
                onWheel={stopEv}
                onTouchMove={stopEv}
                onScroll={stopEv}
            >
                <div className="pp-head">
                    <div className="pp-title">
                        <div className="ico"><IconSparkle /></div>
                        <div>
                            <h1>{AntLocalization.translateText({ vi: 'Phân tích tài liệu AI', en: 'AI Document Analysis', fr: 'Analyse IA de documents', es: 'Análisis de documentos IA', id: 'Analisis Dokumen AI', ja: 'AI 文書解析', zh: 'AI 文档分析', ko: 'AI 문서 분석', hi: 'AI दस्तावेज़ विश्लेषण', ar: 'تحليل المستندات بالذكاء' })}</h1>
                            {/* <div className="sub">{AntLocalization.translateText(SUBTITLES[stage] || SUBTITLES.upload)}</div> */}
                        </div>
                    </div>
                    <button className="pp-close" onClick={handleClose} aria-label="Close"><IconClose /></button>
                </div>

                {/* Stepper chỉ hiển thị ở 2 stage đầu (Upload → Analyze). Khi đến
                    Review thì chuyển sang TAB-LAYOUT (header + items) — Stage3TabbedReview
                    tự render tabs riêng để xem lại + chỉnh sửa phân tích cùng 1 phiếu
                    nhiều lần mà không bị stepper chiếm chỗ. */}
                {stage !== 'review' && <Stepper stage={stage} />}

                {error && <div className="ant-aiva-error">{error}</div>}
                {stage === 'upload' && (
                    <Stage1Upload
                        session={props.session}
                        targetTable={(runtimeTarget && runtimeTarget.targetTable) || props.targetTable}
                        targetId={(runtimeTarget && runtimeTarget.targetId) || props.targetId}
                        targetCode={(runtimeTarget && runtimeTarget.targetCode) || props.targetCode}
                        initialFile={initialFile}
                        onConsumedInitialFile={function () { setInitialFile(null); }}
                        mode={mode}
                        onModeChange={setMode}
                        onAnalyzeStart={handleAnalyzeStart}
                        onAnalyzed={handleAnalyzed}
                        onAnalyzeFailed={handleAnalyzeFailed}
                        onClose={handleClose}
                    />
                )}
                {stage === 'processing' && (
                    <Stage2Processing
                        analysisId={analysisId}
                        mode={mode}
                        session={props.session}
                        onDone={function (data) { setAnalysis(data); setStage('review'); }}
                        onFailed={function (data) {
                            setError(data && (data.error_message || data.message) || AntLocalization.translateText({ vi: 'Phân tích thất bại', en: 'Analysis failed', fr: 'Échec de l’analyse', es: 'Error en el análisis', id: 'Analisis gagal', ja: '解析に失敗しました', zh: '分析失败', ko: '분석 실패', hi: 'विश्लेषण विफल', ar: 'فشل التحليل' }));
                            setStage('upload');
                        }}
                        onClose={handleClose}
                    />
                )}
                {stage === 'review' && analysis && (
                    <Stage3TabbedReview
                        analysis={analysis}
                        session={props.session}
                        onChange={setAnalysis}
                        onApplied={handleApplied}
                        onClose={handleClose}
                        tabLabels={props.tabLabels}
                        headerRenderer={props.headerRenderer}
                    />
                )}
            </div>
        </div>
    );
    return ReactDOM.createPortal(portalNode, document.body);
});

AntAIVoucherAnalyzer.displayName = 'AntAIVoucherAnalyzer';
export default AntAIVoucherAnalyzer;
